import {
  Button,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { Outlet, useParams } from 'react-router';
import { LeftPanelSectionTemplate } from '../../../components/AdminTemplates';
import OutletContextReliantRoute from '../../../components/OutletContextReliantRoute';
import { SideActionItem } from '../../../components/SideActionsList';
import { useLanguage } from '../../../contexts/language';
import { StatusIcon } from '../../../utils/icon';
import { IslandTemplateContext } from './types';
import IslandVisibilityBadge from './IslandVisibilityBadge';
import { Island } from '../../../types/island';
import { useAppDataContext } from '../../../contexts/appData';
import IslandVisibilityToggleDialog from './IslandVisibilityToggleDialog';

const IslandTemplate = () => {
  const { t, language } = useLanguage();
  const { islandId: islandIdQueryParam } = useParams();
  const { islands, refetchIslandAndPortData, isFetching } = useAppDataContext();
  const [displayedIslandName, setDisplayedIslandName] = useState<string>('');
  const isMdUp = useMediaQuery('(min-width:768px)');

  const [toggleVisibilityIsland, setToggleVisbilityIsland] =
    useState<Island | null>(null);

  const islandId = useMemo(() => {
    return typeof islandIdQueryParam !== 'string' ||
      islandIdQueryParam.toLocaleLowerCase() === 'create'
      ? null
      : islandIdQueryParam;
  }, [islandIdQueryParam]);

  const island = useMemo(() => {
    const islandId =
      islandIdQueryParam && islandIdQueryParam.toLocaleLowerCase() !== 'create'
        ? islandIdQueryParam
        : null;
    if (!islandId) {
      return null;
    }
    return islands.find(({ id }) => id === islandId) ?? null;
  }, [islandIdQueryParam, islands]);

  const contextValue = useMemo<IslandTemplateContext>(
    () => ({
      islandId: islandId,
      island: island,
      refetch: () => void refetchIslandAndPortData(),
      setDisplayedIslandName: setDisplayedIslandName,
      isFetching,
    }),
    [islandId, island, isFetching, refetchIslandAndPortData],
  );

  const sidePanelActions = useMemo<SideActionItem[]>(() => {
    return [
      {
        id: 'information',
        label: t('content.information'),
        statusIcon: island ? StatusIcon.Completed : StatusIcon.Pending,
        url: `/admin/islands/${islandId ?? 'create'}`,
        isExactUrl: true,
      },
    ];
  }, [t, island, islandId]);

  const isUsingTheNetwork = isFetching;

  return (
    <LeftPanelSectionTemplate
      title={
        island ? (
          <span className="inline-flex flex-wrap items-center gap-2 md:items-end md:gap-6">
            {`${island.names[language]}`}
            <IslandVisibilityBadge
              isVisible={island.isPublicVisible}
              small={!isMdUp}
            />
          </span>
        ) : (
          displayedIslandName.trim() || t('content.untitled')
        )
      }
      isLoading={isUsingTheNetwork}
      leftPanelActions={sidePanelActions}
      goBackLinkText={t('content.back-to-islands')}
      goBackLinkUrl="/admin/islands"
      actions={
        island && (
          <>
            <Tooltip title={t(`content.island-visibility-tooltip`)}>
              <Button
                variant="outlined"
                onClick={() => setToggleVisbilityIsland(island)}
              >
                {t(
                  island.isPublicVisible
                    ? 'content.hide-island'
                    : 'content.show-island',
                )}
              </Button>
            </Tooltip>
            <IslandVisibilityToggleDialog
              isOpen={Boolean(toggleVisibilityIsland)}
              islandId={island.id}
              isPublicVisible={island.isPublicVisible}
              onClose={() => setToggleVisbilityIsland(null)}
              onConfirm={refetchIslandAndPortData}
            />
          </>
        )
      }
    >
      <Outlet context={contextValue} />
    </LeftPanelSectionTemplate>
  );
};

export default IslandTemplate;

export const IslandSubPageWrapper = () => (
  <OutletContextReliantRoute<IslandTemplateContext>
    getOutletContextState={({ island, islandId, isFetching }) =>
      // If the island has been deleted, it should not be accessible
      island ? 'ready' : islandId && isFetching ? 'pending' : 'error'
    }
    errorRedirectionPath="/admin/islands"
  />
);
